import React, { useState, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import InputField from './InputField';
import { validateFields } from '../lib/validators';
import ErrorBoundary from './ErrorBoundary';
import PayoutModal from './PayoutModal';
import OcrFillButton from './OcrFillButton';
import { sendEvent } from '../analytics/ga4';
import { saveRun } from '../lib/history';
import Toast from './Toast';
import { useToast } from '../hooks/useToast';
import { computeRotationRate } from '../lib/rotationRate';
import { sanitizeOptionalText } from '../lib/sanitize';

const InputForm = ({ mode }) => {
  const isDetail = mode === 'detail';
  const optionalSectionId = `${mode}-optional-section`;
  const storeInputId = `${mode}-store-name`;
  const machineInputId = `${mode}-machine-name`;

  // 入力フィールド用のstate
  const [normalSpins, _setNormalSpins] = useState('');
  const [totalOutput, _setTotalOutput] = useState('');
  const [finalDifference, _setFinalDifference] = useState('');
  const [chanceSpins, _setChanceSpins] = useState('');
  const [decreaseRate, _setDecreaseRate] = useState('');
  const [ballsPer1000Yen, _setBallsPer1000Yen] = useState('');
  const [rotationRate, setRotationRate] = useState(null);
  const [storeName, setStoreName] = useState('');
  const [machineName, setMachineName] = useState('');
  const [isOptionalOpen, setIsOptionalOpen] = useState(false);

  // 保存可否 & 実投資
  const [canSave, setCanSave] = useState(false);
  const [actualInvestment, setActualInvestment] = useState(null);

  // モーダル用（detailのみ）
  const [payoutText, setPayoutText] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [payoutSum, setPayoutSum] = useState(null);
  const [ocrFillConfidence, setOcrFillConfidence] = useState(null);
  const [ocrFillWarning, setOcrFillWarning] = useState('');
  const [ocrFillError, setOcrFillError] = useState('');

  // バリデーション表示用
  const [errors, setErrors] = useState({});
  const [warnings, setWarnings] = useState({});

  // Toast
  const { toast, showToast } = useToast();

  // 履歴復元用
  const location = useLocation();
  const navigate = useNavigate();

  // 定数（simpleのみ使用）
  const SIMPLE_DECREASE_RATE = 0.015;
  const SIMPLE_BALLS_PER_1000 = 250;

  // 入力変更で即「保存不可」にするラッパー
  const setNormalSpins = (v) => {
    _setNormalSpins(v);
    setCanSave(false);
  };
  const setTotalOutput = (v) => {
    _setTotalOutput(v);
    setCanSave(false);
  };
  const setFinalDifference = (v) => {
    _setFinalDifference(v);
    setCanSave(false);
  };
  const setChanceSpins = (v) => {
    _setChanceSpins(v);
    setCanSave(false);
  };
  const setDecreaseRate = (v) => {
    _setDecreaseRate(v);
    setCanSave(false);
  };
  const setBallsPer1000Yen = (v) => {
    _setBallsPer1000Yen(v);
    setCanSave(false);
  };

  const handleStoreNameChange = (v) => {
    setStoreName(sanitizeOptionalText(v));
  };

  const handleMachineNameChange = (v) => {
    setMachineName(sanitizeOptionalText(v));
  };

  // 履歴からの復元
  useEffect(() => {
    const r = location.state?.restore;
    if (!r || r.mode !== mode) return;

    setNormalSpins((r.normalSpins ?? '').toString());
    setTotalOutput((r.totalOutput ?? '').toString());
    setFinalDifference((r.finalDifference ?? '').toString());
    setStoreName(sanitizeOptionalText(r.storeName ?? ''));
    setMachineName(sanitizeOptionalText(r.machineName ?? ''));
    if (r.storeName || r.machineName) {
      setIsOptionalOpen(true);
    }
    if (isDetail) {
      setChanceSpins((r.chanceSpins ?? '').toString());
      setDecreaseRate((r.decreaseRate ?? '').toString());
      setBallsPer1000Yen((r.ballsPer1000 ?? '').toString());
    }

    // 一度使ったstateをクリア（戻る時の二重適用防止）
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate, mode, isDetail]); // eslint-disable-line react-hooks/exhaustive-deps

  // 計算処理
  const calculate = () => {
    try {
      setRotationRate(null);
      setActualInvestment(null);

      const totalOutputNum = parseFloat(totalOutput);
      const finalDifferenceNum = parseFloat(finalDifference);
      const normalSpinsNum = parseFloat(normalSpins);
      const chanceSpinsNum = isDetail ? parseFloat(chanceSpins) : 0;
      const decreaseRateNum = isDetail ? parseFloat(decreaseRate) : SIMPLE_DECREASE_RATE;
      const ballsPer1000YenNum = isDetail ? parseFloat(ballsPer1000Yen) : SIMPLE_BALLS_PER_1000;

      const calcResult = computeRotationRate({
        normalSpins: normalSpinsNum,
        totalOutput: totalOutputNum,
        finalDifference: finalDifferenceNum,
        chanceSpins: chanceSpinsNum,
        decreaseRate: decreaseRateNum,
        ballsPer1000Yen: ballsPer1000YenNum,
        isDetail,
      });

      if (!calcResult) {
        setRotationRate('計算不能（投資が0以下）');
        setCanSave(false);
      } else {
        const { rate, investment } = calcResult;
        const rounded = Math.round(rate * 100) / 100; // 小数第2位で四捨五入
        setActualInvestment(investment); // 保存用
        setRotationRate(rounded);
        setCanSave(true); // 計算成功 → 保存可能
      }
    } catch (err) {
      console.error('[calculate] unexpected', err);
      setRotationRate('計算中に予期せぬエラーが発生しました。');
      setCanSave(false);
    }
  };

  const handleModalConfirm = useCallback(({ text, sum }) => {
    setPayoutText(text);
    setTotalOutput(String(Math.round(sum))); // 必要なら小数も可
    setIsModalOpen(false);
    setPayoutSum(sum);
    setCanSave(false); // 入力変化扱い
    setOcrFillConfidence(null);
    setOcrFillWarning('');
    setOcrFillError('');
  }, []);

  const handleClearAll = useCallback(() => {
    _setNormalSpins('');
    _setTotalOutput('');
    _setFinalDifference('');
    setRotationRate(null);
    setPayoutText('');
    setPayoutSum(null);
    setOcrFillConfidence(null);
    setOcrFillWarning('');
    setOcrFillError('');
    setErrors({});
    setWarnings({});
    setCanSave(false);
    setActualInvestment(null);
    setStoreName('');
    setMachineName('');
    setIsOptionalOpen(false);
    if (isDetail) {
      _setChanceSpins('');
      _setDecreaseRate('');
      _setBallsPer1000Yen('');
    }
  }, [isDetail]);

  // まず検証→OKなら計算
  const handleValidateAndCalc = () => {
    try {
      const {
        isValid,
        errors: e,
        warnings: w,
      } = validateFields(
        {
          normalSpins,
          totalOutput,
          finalDifference,
          chanceSpins: isDetail ? chanceSpins : 0,
          decreaseRate: isDetail ? decreaseRate : SIMPLE_DECREASE_RATE,
          ballsPer1000Yen: isDetail ? ballsPer1000Yen : SIMPLE_BALLS_PER_1000,
          payoutTextSum: isDetail ? payoutSum : null,
        },
        { mode }
      );
      setErrors(e);
      setWarnings(w);
      if (!isValid) {
        setRotationRate('入力エラーがあります。赤字の項目を修正してください。');
        setCanSave(false);
        return;
      }
      calculate();
    } catch (err) {
      console.error('[validate] unexpected', err);
      setRotationRate('予期せぬエラーが発生しました。入力値を見直して再実行してください。');
      setCanSave(false);
    }
  };

  // 保存（LocalStorage）
  const handleSave = () => {
    try {
      if (!(typeof rotationRate === 'number' && isFinite(rotationRate))) return;

      saveRun({
        mode,
        rotationRate,
        normalSpins: Number(normalSpins),
        chanceSpins: isDetail ? Number(chanceSpins) : '',
        totalOutput: Number(totalOutput),
        finalDifference: Number(finalDifference),
        decreaseRate: isDetail ? Number(decreaseRate) : '',
        ballsPer1000: isDetail ? Number(ballsPer1000Yen) : SIMPLE_BALLS_PER_1000,
        actualInvestment: actualInvestment ?? null,
        storeName: sanitizeOptionalText(storeName),
        machineName: sanitizeOptionalText(machineName),
      });

      setCanSave(false); // 再編集するまで再保存不可
      showToast('保存しました（履歴に追加）', {
        type: 'success',
        duration: 2000,
      });
      sendEvent?.('history_save_success', { mode });
    } catch (e) {
      console.error('[history] save failed', e);
      showToast('保存に失敗しました', { type: 'error' });
      sendEvent?.('history_save_error', { mode });
    }
  };

  return (
    <ErrorBoundary>
      <div className="p-6 max-w-xl mx-auto bg-white rounded-xl shadow-md border border-blue-100">
        <h2 className="text-xl font-bold text-blue-800 text-center mb-4">
          {isDetail ? '詳細入力フォーム' : '簡易入力フォーム'}
        </h2>
        {errors._form && (
          <div className="text-red-700 text-sm font-semibold mb-2">{errors._form}</div>
        )}

        <InputField
          label="通常時回転数"
          value={normalSpins}
          onChange={setNormalSpins}
          tooltipImageSrc="img/normal_spins_sample.png"
          tooltipText="この画像の赤枠部分が「通常時回転数」に該当します。"
        />
        {errors.normalSpins && (
          <div className="text-red-600 text-sm mt-1">{errors.normalSpins}</div>
        )}

        {isDetail && (
          <>
            <InputField
              label="チャンス中回転数"
              value={chanceSpins}
              onChange={setChanceSpins}
              tooltipImageSrc="img/chance_spins_sample.png"
              tooltipText="この画像の赤枠部分が「チャンス中回転数」に該当します。"
            />
            {errors.chanceSpins && (
              <div className="text-red-600 text-sm mt-1">{errors.chanceSpins}</div>
            )}
          </>
        )}

        <div className="mb-3">
          <InputField
            label="獲得出玉（獲得数の合計）"
            value={totalOutput}
            onChange={setTotalOutput}
            tooltipImageSrc="img/total_output_sample.png"
            tooltipText="この画像の赤枠部分が「獲得出玉（獲得数の合計）」に該当します。"
          />
          {errors.totalOutput && (
            <div className="text-red-600 text-sm mt-1">{errors.totalOutput}</div>
          )}
          {warnings.totalOutput && (
            <div className="text-yellow-700 text-sm mt-1">{warnings.totalOutput}</div>
          )}
          {isDetail && (
            <>
              <div className="mt-2 flex flex-wrap items-center justify-end gap-3">
                <OcrFillButton
                  tooltipText="数字一覧のみ取り込み可能"
                  tooltipImageSrc="img/ocr_sample.png"
                  onStart={() => {
                    setOcrFillConfidence(null);
                    setOcrFillWarning('');
                    setOcrFillError('');
                  }}
                  onApply={(total, result) => {
                    const rounded = Math.round(total);
                    setTotalOutput(String(Number.isFinite(rounded) ? rounded : total));
                    setPayoutText(result?.sanitizedText ?? '');
                    setPayoutSum(result?.total ?? total);
                    setCanSave(false);
                    setOcrFillError('');
                    setOcrFillConfidence(result?.confidence ?? null);
                    if (result?.confidence != null && result.confidence < 70) {
                      setOcrFillWarning(
                        '画像からの認識結果の信頼度が低い可能性があります。内容をご確認ください。'
                      );
                      showToast('画像の認識結果の信頼度が低い可能性があります。', {
                        type: 'warn',
                        duration: 4000,
                      });
                    } else {
                      setOcrFillWarning('');
                    }
                  }}
                  onError={() => {
                    setOcrFillConfidence(null);
                    setOcrFillWarning('');
                    setOcrFillError('画像の解析に失敗しました。もう一度お試しください。');
                    showToast('画像の解析に失敗しました。もう一度お試しください。', {
                      type: 'error',
                      duration: 4000,
                    });
                  }}
                />
                <button
                  className="bg-blue-100 hover:bg-blue-200 text-blue-800 font-semibold py-1.5 px-4 rounded"
                  onClick={() => setIsModalOpen(true)}
                >
                  出玉計算用
                </button>
              </div>
              {ocrFillConfidence != null && (
                <div className="mt-1 text-xs text-gray-600 text-right">
                  解析信頼度: {Math.round(ocrFillConfidence)}%
                </div>
              )}
              {ocrFillWarning && (
                <div className="mt-1 text-xs text-orange-700 text-right">{ocrFillWarning}</div>
              )}
              {ocrFillError && (
                <div className="mt-1 text-xs text-red-700 text-right">{ocrFillError}</div>
              )}
              {payoutText && (
                <div className="mt-1 text-xs text-gray-500 text-right">
                  （内訳が保存されています。再編集は「出玉計算用ボタン」から）
                </div>
              )}
            </>
          )}
        </div>

        <InputField
          label="最終差玉"
          value={finalDifference}
          onChange={setFinalDifference}
          tooltipImageSrc="img/final_difference_sample.png"
          tooltipText="グラフの最終値、赤線の数値が「最終差玉」に該当します。"
        />
        {errors.finalDifference && (
          <div className="text-red-600 text-sm mt-1">{errors.finalDifference}</div>
        )}

        {isDetail && (
          <>
            <InputField
              label="持ち玉減少率（例: 10% = 0.1を入力）"
              value={decreaseRate}
              onChange={setDecreaseRate}
              tooltipImageSrc="img/decrease_rate_sample.png"
              tooltipText="この画像の赤枠部分が「持ち玉減少率」に該当します。"
            />
            {errors.decreaseRate && (
              <div className="text-red-600 text-sm mt-1">{errors.decreaseRate}</div>
            )}

            <InputField
              label="1000円あたりの貸し玉数（通常は250）"
              value={ballsPer1000Yen}
              onChange={setBallsPer1000Yen}
              tooltipImageSrc="img/balls_per_1000yen_sample.png"
              tooltipText="この画像の赤枠部分が「1000円あたりの貸し玉数」に該当します。"
            />
            {errors.ballsPer1000Yen && (
              <div className="text-red-600 text-sm mt-1">{errors.ballsPer1000Yen}</div>
            )}
            {warnings.ballsPer1000Yen && (
              <div className="text-yellow-700 text-sm mt-1">{warnings.ballsPer1000Yen}</div>
            )}
          </>
        )}

        {!isDetail && (
          <div className="text-xs text-gray-500 mt-2">
            値の前提: 貸し玉数 = {SIMPLE_BALLS_PER_1000}
          </div>
        )}

        <div className="mt-6 border-t border-blue-100 pt-4">
          <button
            type="button"
            className="w-full flex items-center justify-between text-sm font-semibold text-blue-800"
            onClick={() => setIsOptionalOpen((v) => !v)}
            aria-expanded={isOptionalOpen}
            aria-controls={optionalSectionId}
          >
            <span>その他（任意）</span>
            <span aria-hidden="true" className="text-lg">
              {isOptionalOpen ? '−' : '＋'}
            </span>
          </button>
          <div
            id={optionalSectionId}
            data-testid="optional-section"
            className={`mt-3 space-y-3 ${isOptionalOpen ? '' : 'hidden'}`}
            aria-hidden={!isOptionalOpen}
          >
            <div>
              <label
                htmlFor={storeInputId}
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                店舗名（任意）
              </label>
              <input
                type="text"
                value={storeName}
                onChange={(e) => handleStoreNameChange(e.target.value)}
                maxLength={100}
                placeholder="例：○○ホール静岡駅南店"
                className="w-full border border-blue-200 focus:border-blue-500 focus:ring-blue-500 rounded px-2 py-1"
                autoComplete="organization"
                id={storeInputId}
              />
            </div>
            <div>
              <label
                htmlFor={machineInputId}
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                機種名（任意）
              </label>
              <input
                type="text"
                value={machineName}
                onChange={(e) => handleMachineNameChange(e.target.value)}
                maxLength={100}
                placeholder="例：エヴァンゲリオン～未来への咆哮～"
                className="w-full border border-blue-200 focus:border-blue-500 focus:ring-blue-500 rounded px-2 py-1"
                autoComplete="off"
                id={machineInputId}
              />
            </div>
          </div>
        </div>

        <div className="text-center flex justify-center gap-4 mt-4">
          <button
            className="bg-blue-800 hover:bg-blue-900 text-white font-semibold py-2 px-6 rounded"
            onClick={() => {
              handleValidateAndCalc();
              sendEvent('calculate_click', { mode });
            }}
          >
            回転率を計算
          </button>

          <button
            className="bg-blue-100 hover:bg-blue-200 text-blue-800 font-semibold py-2 px-6 rounded"
            onClick={handleClearAll}
          >
            クリア
          </button>
        </div>

        {/* 結果と保存ボタン */}
        {rotationRate !== null &&
          (typeof rotationRate === 'number' ? (
            <div className="text-center mt-4">
              <div className="text-lg font-semibold">
                回転率: <span className="text-blue-700">{rotationRate} 回/K</span>
              </div>

              {canSave && (
                <button
                  className="mt-3 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold py-2 px-4 rounded"
                  onClick={handleSave}
                >
                  保存する
                </button>
              )}
            </div>
          ) : (
            <div className="text-center mt-4 text-red-700 font-semibold">{rotationRate}</div>
          ))}

        {/* クロス項目の総合エラー／警告 */}
        {errors._cross && (
          <div className="mt-3 text-red-700 text-sm text-center">{errors._cross}</div>
        )}
        {warnings._cross && (
          <div className="mt-1 text-yellow-700 text-sm text-center">{warnings._cross}</div>
        )}

        {/* モーダル */}
        {isDetail && (
          <PayoutModal
            open={isModalOpen}
            initialText={payoutText}
            onClose={() => setIsModalOpen(false)}
            onConfirm={handleModalConfirm}
          />
        )}

        {/* トースト */}
        <Toast toast={toast} />
      </div>
    </ErrorBoundary>
  );
};

export default InputForm;
